import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { SyncStatusCode } from '@signage-player/data-store';
import { jitter } from '@signage-player/shared-logic';
import { LogLevel } from '@signage-player/contracts';
export class WebsocketContentSync {
    constructor(config, logger, api, authInstance) {
        this.config = config;
        this.logger = logger;
        this.api = api;
        this.authInstance = authInstance;
        this.exiting = false;
        this.echo = null;
        this.channel = null;
        this.channelName = null;
        this.isConnected = false;
        this.isSubscribed = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 5000; // 5 seconds
        this.extendedReconnectDelay = 60000; // 1 minute
        this.isReconnecting = false;
        this.reconnectTimeout = null;
        this.reconnectExtendedTimeout = null;
        // Channel auth retry state
        this.channelAuthRetryAttempts = 0;
        this.maxChannelAuthRetryAttempts = 5;
        this.channelAuthRetryTimeout = null;
        this.channelAuthExtendedRetryTimeout = null;
        this.channelAuthExtendedRetryDelay = 60000; // 1 minute
        // Callbacks for different data syncs
        this.eventlistCallback = null;
        this.deviceCallback = null;
        this.timeCallback = null;
        this.statusCallback = null;
        // Trailing debounce for burst event protection
        this.eventlistDebounceTimeout = null;
        this.eventlistDebouncePending = null;
        this.eventlistDebounceDelay = 1000; // 1 second
        this.informativeDebounceTimeout = null;
        this.informativeDebounceDelay = 3000; // 3 seconds
        if (this.config.dynamicConfig.websocketsEnabled) {
            this.initializeWebsocket();
        }
        else {
            this.logger.info('[WebsocketContentSync] WebSockets are disabled in configuration.');
        }
    }
    // These methods now also trigger the channel subscription
    async initializeEventlistSync(newDataCallback) {
        this.eventlistCallback = newDataCallback;
    }
    async initializeDeviceSync(newDataCallback) {
        this.deviceCallback = newDataCallback;
    }
    async initializeTimeSync(newDataCallback) {
        this.timeCallback = newDataCallback;
    }
    initializeStatusHandler(statusCallback) {
        this.statusCallback = statusCallback;
        if (this.statusCallback && !this.config.dynamicConfig.websocketsEnabled) {
            this.statusCallback({ code: SyncStatusCode.WebSocketDisconnected });
        }
    }
    initializeWebsocket() {
        this.logger.debug('[WebsocketContentSync] Initializing WebSocket connection...');
        // Ensure you have a base URL for the auth endpoint
        const reverbConfig = this.config.dynamicConfig.reverb;
        if (!reverbConfig) {
            this.logger.error('[WebsocketContentSync] Reverb config is not available.');
            return;
        }
        const authUrl = `${this.authInstance.url}/broadcasting/auth`;
        const authToken = this.authInstance.token;
        const options = {
            broadcaster: 'reverb',
            key: reverbConfig.key,
            wsHost: reverbConfig.host,
            wsPort: reverbConfig.port,
            wssPort: reverbConfig.port,
            wsPath: reverbConfig.path ? reverbConfig.path : undefined,
            forceTLS: reverbConfig.scheme === 'https',
            enabledTransports: ['ws', 'wss'],
            cluster: 'signage-player', // Reverb uses appId as cluster
            encrypted: reverbConfig.scheme === 'https',
            // Custom authorizer with retry logic to handle transient network failures
            // (DNS not ready, TCP timeout, TLS handshake failure on wifi/hotspot etc.)
            channelAuthorization: {
                customHandler: (params, callback) => {
                    this.authorizeChannelWithRetry(authUrl, authToken, params.socketId, params.channelName, callback);
                },
            },
            // Pusher-specific options for connection stability
            activityTimeout: 120000, // 2 minutes
            pongTimeout: 30000, // 30 seconds
            unavailableTimeout: 16000, // 16 seconds
        };
        this.echo = new Echo({
            client: new Pusher(reverbConfig.key, options),
            ...options,
        });
        this.setupConnectionHandlers();
    }
    /**
     * Custom channel authorizer with retry logic.
     * Pusher's default auth makes a single HTTP request with no retry.
     * On unstable networks (wifi, hotspot), the auth request can fail with status 0
     * (DNS timeout, TCP connection failure, TLS handshake failure).
     * This method retries the auth request with exponential backoff.
     *
     * Non-retryable errors (401, 403) are failed immediately — these indicate
     * an actual auth problem, not a transient network issue.
     */
    async authorizeChannelWithRetry(authUrl, authToken, socketId, channelName, callback, maxRetries = 3, baseDelay = 1500) {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            // Stop retrying if we're shutting down, or if a *retry* finds us
            // disconnected. We must NOT abort the FIRST attempt on `!isConnected`:
            // pusher-js invokes the channel authorizer during the reconnect handshake,
            // before our `connected` handler flips `isConnected` back to true, so the
            // initial auth would otherwise be aborted by a purely transient false —
            // killing the channel re-subscription after a 1006 reconnect.
            if (this.exiting || (attempt > 0 && !this.isConnected)) {
                this.logger.debug('[WebsocketContentSync] Channel auth aborted (disconnected or exiting).');
                callback(new Error('Channel auth aborted: disconnected or exiting'), null);
                return;
            }
            try {
                const body = JSON.stringify({
                    socket_id: socketId,
                    channel_name: channelName,
                });
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
                const response = await fetch(authUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                    },
                    body,
                    signal: controller.signal,
                });
                clearTimeout(timeoutId);
                if (!response.ok) {
                    // Don't retry auth/permission failures — these won't resolve with retries
                    if (response.status === 401 || response.status === 403) {
                        const msg = `Auth rejected with status ${response.status}: ${response.statusText}`;
                        this.logger.error(`[WebsocketContentSync] ${msg}`);
                        callback(new Error(msg), null);
                        return;
                    }
                    throw new Error(`Auth endpoint returned status ${response.status}: ${response.statusText}`);
                }
                const data = await response.json();
                if (attempt > 0) {
                    this.logger.info(`[WebsocketContentSync] Channel auth succeeded on retry attempt ${attempt + 1}.`);
                }
                callback(null, data);
                return;
            }
            catch (error) {
                const errorMessage = error?.message || String(error);
                if (attempt < maxRetries) {
                    const delay = baseDelay * Math.pow(2, attempt); // 1.5s, 3s, 6s
                    this.logger.warning(`[WebsocketContentSync] Channel auth attempt ${attempt + 1}/${maxRetries + 1} failed, retrying in ${delay}ms...`, {
                        error: errorMessage,
                    });
                    await new Promise(resolve => setTimeout(resolve, jitter(delay)));
                }
                else {
                    this.logger.error(`[WebsocketContentSync] Channel auth failed after ${maxRetries + 1} attempts.`, { error: errorMessage });
                    callback(new Error(`Channel auth failed after ${maxRetries + 1} attempts: ${errorMessage}`), null);
                }
            }
        }
    }
    setupConnectionHandlers() {
        if (!this.echo)
            return;
        const pusher = this.echo.connector.pusher;
        pusher.connection.bind('connected', () => {
            this.isConnected = true;
            this.isReconnecting = false;
            this.reconnectAttempts = 0;
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }
            if (this.reconnectExtendedTimeout) {
                clearTimeout(this.reconnectExtendedTimeout);
                this.reconnectExtendedTimeout = null;
            }
            this.logger.info('[WebsocketContentSync] WebSocket connected successfully!', { id: pusher.connection.socket_id });
            // NOTE: WebSocketConnected (the "healthy, slow the polling" signal) is now
            // emitted from channel.subscribed(), NOT here. The transport being up does
            // not mean we are receiving pushes — only an active presence-channel
            // subscription does. Emitting it here let a silently-dead channel keep the
            // device on the slow 1h polling fallback. See subscribeToChannel().
            if (!this.isSubscribed) {
                this.subscribeToChannel();
            }
        });
        pusher.connection.bind('disconnected', () => {
            this.isConnected = false;
            this.logger.log(this.exiting ? LogLevel.NOTICE : LogLevel.WARNING, '[WebsocketContentSync] WebSocket disconnected.');
            this.cleanupChannel();
            if (this.exiting)
                return;
            if (this.statusCallback) {
                this.statusCallback({ code: SyncStatusCode.WebSocketDisconnected });
            }
            this.handleReconnection();
        });
        pusher.connection.bind('unavailable', () => {
            this.isConnected = false;
            this.logger.warning('[WebsocketContentSync] WebSocket connection unavailable.');
            this.cleanupChannel();
            if (this.statusCallback) {
                this.statusCallback({ code: SyncStatusCode.WebSocketDisconnected });
            }
            this.handleReconnection();
        });
        pusher.connection.bind('error', (error) => {
            this.isConnected = false;
            // 1006 = abnormal closure (transient network drop, no close frame received)
            const code = error?.data?.code;
            const isTransient = code === 1006;
            this.logger.log(isTransient ? LogLevel.WARNING : LogLevel.ERROR, '[WebsocketContentSync] WebSocket connection error.', error);
            if (this.exiting)
                return;
            // Only a 1006 (abnormal closure) needs special handling here: pusher-js
            // auto-reconnects from it WITHOUT firing a `disconnected` event, so
            // cleanupChannel() would otherwise never run and `isSubscribed` would stay
            // true — the following `connected` event then skips the re-subscribe
            // (`if (!this.isSubscribed)`) and the presence channel is silently dead for
            // the rest of the run, leaving the device on the slow 1h polling fallback.
            // Tear the channel down so the reconnect re-subscribes, and report
            // disconnected so the ContentSyncManager drops back to fast polling until
            // the channel is actually re-subscribed.
            //
            // Every OTHER error must NOT tear the channel down here. A *closing* error
            // (refused/backoff codes) is already covered by the `disconnected` /
            // `unavailable` handlers (cleanup + handleReconnection). A *non-closing*
            // error (e.g. a MessageParseError, or a server `pusher:error` on an
            // otherwise-live socket) leaves the connection up — pusher fires no
            // follow-up `connected`/`disconnected`, so cleaning up here would silently
            // and unrecoverably kill a working channel and pin the device to fast
            // polling for the rest of the run.
            if (!isTransient)
                return;
            this.cleanupChannel();
            if (this.statusCallback) {
                this.statusCallback({ code: SyncStatusCode.WebSocketDisconnected });
            }
        });
        pusher.connection.bind('state_change', (states) => {
            this.logger.debug(`[WebsocketContentSync] Connection state changed: ${states.previous} -> ${states.current}`);
        });
    }
    handleReconnection() {
        // Guard: only one reconnection chain at a time
        if (this.isReconnecting) {
            this.logger.debug('[WebsocketContentSync] Reconnection already in progress, skipping duplicate call.');
            return;
        }
        this.isReconnecting = true;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.reconnectExtendedTimeout) {
            clearTimeout(this.reconnectExtendedTimeout);
            this.reconnectExtendedTimeout = null;
        }
        this.attemptReconnect();
    }
    attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.logger.warning('[WebsocketContentSync] Max initial reconnection attempts reached. Switching to extended retry every minute.');
            // Fast-retry chain is over; the extended chain takes over autonomously.
            // Reset the counter and release the guard so a future `disconnected` event can start a new fast chain
            // (without this reset, the next disconnect would re-enter, immediately fall into this branch, and the fast burst would never run).
            this.reconnectAttempts = 0;
            this.isReconnecting = false;
            this.scheduleExtendedReconnection();
            return;
        }
        this.reconnectAttempts++;
        this.logger.info(`[WebsocketContentSync] Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        if (this.echo) {
            try {
                this.echo.connect();
            }
            catch (err) {
                this.logger.error('[WebsocketContentSync] echo.connect() threw during reconnection attempt.', { error: err });
            }
        }
        // Schedule next attempt if not connected
        this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            if (this.exiting || this.isConnected)
                return;
            // If handleReconnection released the guard while we were waiting (e.g. it
            // ran a fresh chain that has since finished), don't re-enter the fast loop.
            if (!this.isReconnecting)
                return;
            this.attemptReconnect();
        }, jitter(this.reconnectDelay));
    }
    scheduleExtendedReconnection() {
        if (this.reconnectExtendedTimeout) {
            clearTimeout(this.reconnectExtendedTimeout);
            this.reconnectExtendedTimeout = null;
        }
        this.reconnectExtendedTimeout = setTimeout(() => {
            this.reconnectExtendedTimeout = null;
            // Bail if shutdown started or a fast-retry chain has taken over (handleReconnection
            // ran while we were waiting). Without this guard, both chains end up running
            // concurrently after echo.connect() synchronously emits a disconnect/error event.
            if (this.exiting || this.isReconnecting || this.isConnected || !this.echo)
                return;
            this.logger.debug('[WebsocketContentSync] Attempting extended reconnection...');
            try {
                this.echo.connect();
            }
            catch (err) {
                this.logger.error('[WebsocketContentSync] echo.connect() threw during extended reconnection.', { error: err });
            }
            // echo.connect() can synchronously fire a disconnect/error which re-enters
            // handleReconnection; re-check before scheduling the next iteration.
            if (this.exiting || this.isReconnecting || this.isConnected)
                return;
            this.scheduleExtendedReconnection();
        }, jitter(this.extendedReconnectDelay));
    }
    cleanupChannel() {
        // Cancel pending debounce timers
        if (this.eventlistDebounceTimeout) {
            clearTimeout(this.eventlistDebounceTimeout);
            this.eventlistDebounceTimeout = null;
            this.eventlistDebouncePending = null;
        }
        if (this.informativeDebounceTimeout) {
            clearTimeout(this.informativeDebounceTimeout);
            this.informativeDebounceTimeout = null;
        }
        // Always cancel pending auth retries when cleaning up channel
        if (this.channelAuthRetryTimeout) {
            clearTimeout(this.channelAuthRetryTimeout);
            this.channelAuthRetryTimeout = null;
        }
        if (this.channelAuthExtendedRetryTimeout) {
            clearTimeout(this.channelAuthExtendedRetryTimeout);
            this.channelAuthExtendedRetryTimeout = null;
        }
        this.channelAuthRetryAttempts = 0;
        if (this.channel) {
            this.logger.debug('[WebsocketContentSync] Cleaning up existing channel.');
            // Release the channel via echo.leave(). laravel-echo CACHES the presence
            // channel in its connector and `echo.join()` returns that same cached
            // object, so merely nulling `this.channel` leaks every listener we bind on
            // each re-subscribe. In particular `stopListening('subscribed'|'error')`
            // does NOT unbind the `.subscribed()`/`.error()` callbacks: the
            // EventFormatter maps those names to `App\Events\subscribed` /
            // `App\Events\error`, whereas laravel-echo binds the literal pusher events
            // `pusher:subscription_succeeded` / `pusher:subscription_error`. leave()
            // unsubscribes and evicts the cached channel, so the next join() builds a
            // fresh one with fresh bindings instead of accumulating handlers on every
            // 1006 reconnect.
            if (this.channelName) {
                this.echo?.leave(this.channelName);
            }
            this.channel = null;
            this.channelName = null;
            this.isSubscribed = false;
        }
    }
    subscribeToChannel() {
        if (!this.echo || !this.isConnected) {
            this.logger.info('[WebsocketContentSync] Cannot subscribe, Echo not ready or not connected.');
            return;
        }
        const reverbConfig = this.config.dynamicConfig.reverb;
        if (!reverbConfig || !reverbConfig.channel) {
            this.logger.error('[WebsocketContentSync] Channel name is not defined in the configuration.');
            return;
        }
        const channelName = reverbConfig.channel;
        // Clean up existing channel if any
        this.cleanupChannel();
        this.channelName = channelName;
        this.logger.info(`[WebsocketContentSync] Subscribing to presence channel: ${channelName}`);
        // Use 'join' for presence channels
        this.channel = this.echo.join(channelName);
        this.channel.subscribed(() => {
            if (!this.isSubscribed) {
                this.isSubscribed = true;
                this.channelAuthRetryAttempts = 0; // Reset retry counter on success
                this.logger.info(`[WebsocketContentSync] Successfully subscribed to channel: ${channelName}`);
                // Only NOW are we genuinely receiving real-time pushes — this is the
                // signal that lets ContentSyncManager switch to the slow polling cadence.
                if (this.statusCallback) {
                    this.statusCallback({ code: SyncStatusCode.WebSocketConnected });
                }
            }
        });
        this.channel.error((error) => {
            /*const isNetworkError = String(error).includes('received status: 0');
            this.logger.log(isNetworkError ? LogLevel.WARNING : LogLevel.ERROR, `[WebsocketContentSync] Channel error on ${channelName}.`, error);
            this.handleReconnection();*/
            const errorStr = String(error?.error || error);
            const isAuthNetworkError = errorStr.includes('received status: 0') || error?.type === 'AuthError';
            this.logger.log(isAuthNetworkError ? LogLevel.NOTICE : LogLevel.ERROR, `[WebsocketContentSync] Channel error on ${channelName}.`, error);
            // Retry channel subscription on auth network errors (status 0 = request never completed)
            if (isAuthNetworkError && this.isConnected && !this.exiting) {
                this.scheduleChannelAuthRetry();
            }
        });
        this.setupEventListeners(this.channel);
    }
    /**
     * Schedule a retry for channel subscription after an auth failure.
     * Uses exponential backoff: 3s, 6s, 12s, 24s, 48s
     * After max attempts, switches to extended retry every 60s (app runs nonstop).
     */
    scheduleChannelAuthRetry() {
        if (this.channelAuthRetryAttempts >= this.maxChannelAuthRetryAttempts) {
            this.logger.warning(`[WebsocketContentSync] Max channel auth retry attempts (${this.maxChannelAuthRetryAttempts}) reached. ` +
                'Switching to extended channel auth retry every 60s.');
            // Notify that WS is effectively disconnected (channel not subscribed)
            if (this.statusCallback) {
                this.statusCallback({ code: SyncStatusCode.WebSocketDisconnected });
            }
            this.scheduleExtendedChannelAuthRetry();
            return;
        }
        this.channelAuthRetryAttempts++;
        const delay = 3000 * Math.pow(2, this.channelAuthRetryAttempts - 1);
        this.logger.info(`[WebsocketContentSync] Scheduling channel subscription retry in ${delay}ms ` +
            `(attempt ${this.channelAuthRetryAttempts}/${this.maxChannelAuthRetryAttempts})`);
        this.channelAuthRetryTimeout = setTimeout(() => {
            this.channelAuthRetryTimeout = null;
            if (this.isConnected && !this.exiting && !this.isSubscribed) {
                this.subscribeToChannel();
            }
        }, jitter(delay));
    }
    /**
     * Long-term retry for channel auth. The app is meant to run nonstop,
     * so we never permanently give up — retry every 60s indefinitely
     * as long as the WebSocket connection itself is alive.
     */
    scheduleExtendedChannelAuthRetry() {
        this.channelAuthExtendedRetryTimeout = setTimeout(() => {
            this.channelAuthExtendedRetryTimeout = null;
            if (this.isConnected && !this.exiting && !this.isSubscribed) {
                this.logger.info('[WebsocketContentSync] Attempting extended channel auth retry...');
                // Reset attempts counter so the fast backoff cycle runs again
                this.channelAuthRetryAttempts = 0;
                this.subscribeToChannel();
            }
        }, jitter(this.channelAuthExtendedRetryDelay));
    }
    /**
     * Check if an event is targeted at this device.
     * If the event has a non-empty `target_device_hash`, it must match this device's hash.
     * Events without a target (null, undefined, empty string) are processed by all devices.
     */
    isEventTargetedAtThisDevice(event) {
        const targetHash = event?.target_device_hash;
        if (targetHash === null || targetHash === undefined || targetHash === '' || !targetHash) {
            return true;
        }
        return targetHash === this.authInstance.hash;
    }
    setupEventListeners(channel) {
        // Trailing debounce: coalesce burst updates, only process the latest payload after 1s of quiet
        channel.listen('.eventlist.updated', (event) => {
            this.logger.debug('[WebsocketContentSync] Received "eventlist.updated" event.');
            if (!this.isEventTargetedAtThisDevice(event)) {
                this.logger.debug('[WebsocketContentSync] Skipping "eventlist.updated" event — targeted at another device.');
                return;
            }
            if (!this.eventlistCallback) {
                this.logger.warning('[WebsocketContentSync] No callback registered for eventlist updates.');
                return;
            }
            this.eventlistDebouncePending = event;
            if (this.eventlistDebounceTimeout) {
                clearTimeout(this.eventlistDebounceTimeout);
            }
            this.eventlistDebounceTimeout = setTimeout(() => {
                this.eventlistDebounceTimeout = null;
                if (this.eventlistCallback && this.eventlistDebouncePending) {
                    this.eventlistCallback(this.eventlistDebouncePending);
                    this.eventlistDebouncePending = null;
                }
            }, this.eventlistDebounceDelay);
        });
        // Trailing debounce: coalesce burst informative events, only fetch from API after 3s of quiet
        channel.listen('.eventlist.updated_informative', (event) => {
            this.logger.debug('[WebsocketContentSync] Received "eventlist.updated_informative" event.');
            if (!this.isEventTargetedAtThisDevice(event)) {
                this.logger.debug('[WebsocketContentSync] Skipping "eventlist.updated_informative" event — targeted at another device.');
                return;
            }
            if (!this.eventlistCallback) {
                this.logger.warning('[WebsocketContentSync] No callback registered for eventlist updates.');
                return;
            }
            if (this.informativeDebounceTimeout) {
                clearTimeout(this.informativeDebounceTimeout);
            }
            this.informativeDebounceTimeout = setTimeout(() => {
                this.informativeDebounceTimeout = null;
                if (this.eventlistCallback) {
                    this.api
                        .getEventlistsForAllDisplays()
                        .then(eventList => {
                        this.logger.debug('[WebsocketContentSync] Eventlist fetched from API.');
                        if (this.eventlistCallback) {
                            this.eventlistCallback(eventList);
                        }
                    })
                        .catch(error => {
                        this.logger.warning('[WebsocketContentSync] Error fetching eventlist from API.', { error });
                    });
                }
            }, this.informativeDebounceDelay);
        });
        channel.listen('.device.updated', (event) => {
            this.logger.debug('[WebsocketContentSync] Received "device.updated" event.');
            if (!this.isEventTargetedAtThisDevice(event)) {
                this.logger.debug('[WebsocketContentSync] Skipping "device.updated" event — targeted at another device.');
                return;
            }
            if (this.deviceCallback) {
                this.deviceCallback(event);
            }
            else {
                this.logger.warning('[WebsocketContentSync] No callback registered for device updates.');
            }
        });
        channel.listen('.device.disconnected', (event) => {
            this.logger.warning('[WebsocketContentSync] Received "device.disconnected" event.', event);
            if (!this.isEventTargetedAtThisDevice(event)) {
                this.logger.debug('[WebsocketContentSync] Skipping "device.disconnected" event — targeted at another device.');
                return;
            }
            if (this.statusCallback) {
                this.statusCallback({ code: SyncStatusCode.Unauthorized });
            }
            else {
                this.logger.warning('[WebsocketContentSync] No callback registered for device disconnect.');
            }
        });
    }
    disconnect() {
        this.exiting = true;
        this.isReconnecting = false;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.reconnectExtendedTimeout) {
            clearTimeout(this.reconnectExtendedTimeout);
            this.reconnectExtendedTimeout = null;
        }
        if (this.channelAuthRetryTimeout) {
            clearTimeout(this.channelAuthRetryTimeout);
            this.channelAuthRetryTimeout = null;
        }
        if (this.channelAuthExtendedRetryTimeout) {
            clearTimeout(this.channelAuthExtendedRetryTimeout);
            this.channelAuthExtendedRetryTimeout = null;
        }
        if (this.eventlistDebounceTimeout) {
            clearTimeout(this.eventlistDebounceTimeout);
            this.eventlistDebounceTimeout = null;
            this.eventlistDebouncePending = null;
        }
        if (this.informativeDebounceTimeout) {
            clearTimeout(this.informativeDebounceTimeout);
            this.informativeDebounceTimeout = null;
        }
        if (this.echo) {
            this.logger.info('[WebsocketContentSync] Disconnecting WebSocket...');
            this.cleanupChannel();
            this.echo.disconnect();
            this.echo = null;
            this.isConnected = false;
        }
    }
    destroy() {
        this.logger.debug('[WebsocketContentSync] Destroying WebSocket content sync...');
        this.disconnect();
    }
}
//# sourceMappingURL=WebsocketContentSync.js.map